/**
 * Standardized toast notification patterns for RetireEZ.
 *
 * Wraps react-hot-toast with consistent duration, styling, and
 * positioning rules defined in FS-014 WP-5 / RWP-E.
 *
 * Usage:
 *   import { toastPatterns } from "@/utils/toastPatterns";
 *   toastPatterns.success("Changes saved");
 *   toastPatterns.error("Something went wrong");
 *   toastPatterns.autoSave();
 */
import toast from "react-hot-toast";

/** Duration constants (milliseconds) */
const DURATION = {
  autoSave: 2000,
  success: 3000,
  info: 3000,
  formSubmit: 3000,
  error: 5000,
  persistent: Infinity,
} as const;

export const toastPatterns = {
  /**
   * Generic success toast (teal, 3s).
   */
  success(message: string) {
    return toast.success(message, { duration: DURATION.success });
  },

  /**
   * Auto-save confirmation (teal, 2s).
   * Shows "✓ Saved" — brief and unobtrusive.
   */
  autoSave() {
    return toast.success("✓ Saved", { duration: DURATION.autoSave });
  },

  /**
   * Form submission success (teal, 3s).
   */
  formSubmit(message: string) {
    return toast.success(message, { duration: DURATION.formSubmit });
  },

  /**
   * Error toast (red, 5s).
   */
  error(message: string) {
    return toast.error(message, { duration: DURATION.error });
  },

  /**
   * Network error toast (red, persistent until manually dismissed).
   */
  networkError(message: string) {
    return toast.error(message, { duration: DURATION.persistent });
  },

  /**
   * Informational toast (neutral, 3s).
   */
  info(message: string) {
    return toast(message, { duration: DURATION.info });
  },

  /**
   * Dismiss a specific toast by ID, or all toasts if no ID given.
   */
  dismiss(id?: string) {
    return toast.dismiss(id);
  },
} as const;
